import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '../types';
import { API_CONFIG, buildApiUrl } from '../config/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = !!user && !!token;
  // Safely check if user has admin role, handling both old and new formats
  const isAdmin = user?.roles?.some(role => 
    typeof role === 'string' ? role === 'admin' : role.name === 'admin'
  ) || false;

  const login = (newToken: string, newUser: User) => {
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('admin_token', newToken);
    localStorage.setItem('admin_user', JSON.stringify(newUser));
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  };

  const verifyToken = async (storedToken: string) => {
    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.AUTH_VERIFY), {
        headers: {
          'Authorization': `Bearer ${storedToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setToken(storedToken);
        return true;
      } else {
        // Token is invalid
        logout();
        return false;
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
      return false;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('admin_token');
      const storedUser = localStorage.getItem('admin_user');

      if (storedToken && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          
          // Check if roles need migration from string[] to Role[]
          if (parsedUser.roles && parsedUser.roles.length > 0) {
            // If roles are strings, we need to re-fetch user data
            if (typeof parsedUser.roles[0] === 'string') {
              console.log('Migrating user data format...');
              // Verify token and get fresh user data
              const isValid = await verifyToken(storedToken);
              if (!isValid) {
                console.log('Token verification failed during migration');
              }
              setLoading(false);
              return;
            }
          }
          
          // Set user and token immediately from localStorage
          setUser(parsedUser);
          setToken(storedToken);
          
          // Verify token with backend in the background (optional)
          // Don't logout on failure - user can continue with cached data
          fetch(buildApiUrl(API_CONFIG.ENDPOINTS.AUTH_VERIFY), {
            headers: {
              'Authorization': `Bearer ${storedToken}`,
            },
          })
            .then(response => {
              if (response.ok) {
                return response.json();
              }
              throw new Error('Token verification failed');
            })
            .then(data => {
              // Update user data with fresh data from server
              setUser(data.user);
              localStorage.setItem('admin_user', JSON.stringify(data.user));
              console.log('Token verified successfully');
            })
            .catch((error) => {
              console.error('Token verification error:', error);
              console.log('Continuing with cached user data');
              // Don't logout - let user continue with cached data
            });
        } catch (error) {
          console.error('Error parsing stored user data:', error);
          logout();
        }
      }

      setLoading(false);
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add Authorization header to all API requests when authenticated
  useEffect(() => {
    if (token) {
      // Store original fetch
      const originalFetch = window.fetch;

      // Override fetch to include auth header
      window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
        const url = typeof input === 'string' ? input : input.toString();

        // Only add auth header for our API endpoints
        if (url.includes('/api/') && !url.includes('/api/auth/login')) {
          init = init || {};
          init.headers = {
            ...init.headers,
            'Authorization': `Bearer ${token}`,
          };
        }

        return originalFetch(input, init);
      };

      // Cleanup function to restore original fetch
      return () => {
        window.fetch = originalFetch;
      };
    }
  }, [token]);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated,
    isAdmin,
    login,
    logout,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};